import { ReactNode, useMemo } from "react"
import { Checkbox, Flex, Popover, ScrollArea, Text } from "@radix-ui/themes"
import { CaretDownIcon } from "@radix-ui/react-icons"
import type { SelectOption, SelectSize } from "./Select"

export type MultiSelectProps<Opt extends SelectOption> = {
  /** Выбранные значения (строковые ключи). */
  values: string[]
  /** Колбэк изменения. */
  onChange: (values: string[]) => void
  /** Список опций. */
  options: Opt[]
  /** Текст, когда ничего не выбрано. */
  placeholder?: string
  /** Текст опции «Выбрать все». */
  selectAllText?: string
  /** Показывать «Выбрать все». */
  showSelectAll?: boolean
  /** Размер. */
  size?: SelectSize
  /** Опциональная подпись слева. */
  label?: ReactNode
  /** Текст, когда выбрано >1 опции. По умолчанию «Выбрано N». */
  selectedCountText?: (count: number) => string
  /** Доп. класс корневого элемента. */
  className?: string
  /** Стиль корневого элемента. */
  style?: React.CSSProperties
  /** Отключить. */
  disabled?: boolean
  /** Ширина триггера (пробрасывается в обёртку как inline width). */
  width?: number | string
  /** Макс. высота списка. */
  contentMaxHeight?: number | string
}

/**
 * Переиспользуемый multi-select на @radix-ui/themes (Popover + Checkbox + ScrollArea).
 * Заменяет самописный Multiselect с ручным click-outside listener:
 * Popover сам управляет открытием/закрытием и кликом вне.
 *
 * Поведение trigger-подписи сохранено из старого Multiselect:
 *  - 0 выбрано → placeholder
 *  - 1 выбрано → label этой опции
 *  - >1 выбрано → «Выбрано N»
 */
export function MultiSelect<Opt extends SelectOption>({
  values,
  onChange,
  options,
  placeholder = "Выберите варианты...",
  selectAllText = "Выбрать все",
  showSelectAll = true,
  size = "2",
  label,
  selectedCountText = (count) => `Выбрано ${count}`,
  className,
  style,
  disabled,
  width,
  contentMaxHeight = 350,
}: MultiSelectProps<Opt>) {
  // Синхронизируем высоту с Radix Select.Trigger (var(--space-5/6/7) = 24/32/40px).
  const triggerHeight =
    size === "1"
      ? "var(--space-5)"
      : size === "3"
      ? "var(--space-7)"
      : "var(--space-6)"
  const triggerFontSize =
    size === "1"
      ? "var(--font-size-1)"
      : size === "3"
      ? "var(--font-size-3)"
      : "var(--font-size-2)"
  // Сравнение по множествам, а не по длинам: values может содержать ключи,
  // которых уже нет в options (асинхронное обновление списка).
  const optionValues = useMemo(
    () => new Set(options.map((o) => o.value)),
    [options],
  )
  const selectedInOptions = useMemo(
    () => values.filter((v) => optionValues.has(v)),
    [values, optionValues],
  )
  const allSelected =
    options.length > 0 && selectedInOptions.length === options.length

  const toggleOption = (value: string) => {
    const isSelected = values.includes(value)
    onChange(
      isSelected ? values.filter((v) => v !== value) : [...values, value],
    )
  }

  const toggleSelectAll = () => {
    if (allSelected) {
      // Снимаем только те, что есть в options; сторонние значения оставляем.
      onChange(values.filter((v) => !optionValues.has(v)))
    } else {
      // Добавляем все значения из options, не дублируя уже выбранные.
      onChange(Array.from(new Set([...values, ...optionValues])))
    }
  }

  const isOptionSelected = (value: string) => values.includes(value)

  const getTriggerText = (): ReactNode => {
    if (!values.length) return placeholder
    if (values.length === 1) {
      return options.find((o) => o.value === values[0])?.label ?? values[0]
    }
    return selectedCountText(values.length)
  }

  const trigger = (
    <div
      className={className}
      style={{
        position: "relative",
        ...(width != null ? { width } : {}),
        ...style,
      }}
    >
      <Popover.Root>
        {/* Popover.Trigger рендерит <button>. Высота и шрифт синхронизированы
            с Radix Select.Trigger (--space-5/6/7, --font-size-1/2/3). */}
        <Popover.Trigger
          disabled={disabled}
          style={{
            width: "100%",
            height: triggerHeight,
            lineHeight: triggerHeight,
            padding: "4px 8px", // как Radix ghost: --select-trigger-ghost-padding-y/x
            border: "1px solid var(--gray-6)",
            borderRadius: "var(--radius-2)",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
            backgroundColor: "var(--color-surface)",
            color: "var(--gray-12)",
            font: "inherit",
            fontSize: triggerFontSize,
            textAlign: "left",
          }}
        >
          <span
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "8px",
              width: "100%",
            }}
          >
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {getTriggerText()}
            </span>
            <CaretDownIcon style={{ marginLeft: "8px" }} />
          </span>
        </Popover.Trigger>
        <Popover.Content
          style={{ width, padding: 0, maxHeight: contentMaxHeight }}
          sideOffset={4}
        >
          <ScrollArea
            type="auto"
            scrollbars="vertical"
            style={{ maxHeight: contentMaxHeight }}
          >
            {showSelectAll && (
              <Text
                as="label"
                size={size}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  backgroundColor: "var(--gray-2)",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  fontWeight: "bold",
                }}
              >
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={() => toggleSelectAll()}
                />
                {selectAllText}
              </Text>
            )}
            {options.map((option) => (
              <Text
                as="label"
                key={option.value}
                size={size}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  backgroundColor: isOptionSelected(option.value)
                    ? "var(--blue-2)"
                    : "transparent",
                }}
              >
                <Checkbox
                  checked={isOptionSelected(option.value)}
                  onCheckedChange={() => toggleOption(option.value)}
                />
                {option.label}
              </Text>
            ))}
          </ScrollArea>
        </Popover.Content>
      </Popover.Root>
    </div>
  )

  if (label == null) return trigger

  return (
    <Flex gap="2" justify="center" align="center">
      <Text as="label">{label}</Text>
      {trigger}
    </Flex>
  )
}
